import { useState, useMemo } from "react";
import { ColorBlock } from "@/components/page";
import { useResourceList } from "@/components/data";

type PhoneCall = {
  id: number;
  call_num: number | null;
  student_name: string;
  group_name: string | null;
  source: string | null;
  phone: string | null;
  note: string | null;
  status: string | null;
};

const STATUS_DOT: Record<string, string> = {
  pending: "bg-amber-400",
  contacted: "bg-sky-400",
  enrolled: "bg-emerald-400",
  declined: "bg-rose-400",
};

export function PaamonPhoneScriptsPage() {
  const { data, isLoading } = useResourceList<PhoneCall>("paamon_phone_calls");
  const all = data ?? [];

  const buckets = useMemo(() => {
    const s = new Set<string>();
    for (const c of all) if (c.status) s.add(c.status);
    return ["all", ...Array.from(s).sort()];
  }, [all]);

  const [active, setActive] = useState<string>("all");
  const filtered = active === "all" ? all : all.filter((c) => c.status === active);

  return (
    <div className="space-y-6 sm:space-y-7">
        <header className="pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            סקריפט שיחות
          </p>
          <h1 className="mt-3 flex items-baseline gap-3 text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
            <span className="tabular-nums" dir="ltr">{all.length}</span>
            <span className="text-stone-300 text-lg sm:text-xl">·</span>
            <span className="text-stone-700 text-lg sm:text-xl">שיחות</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-stone-600 leading-snug">
            טלפונים לילדים שנרשמו בתוכנייה — מעקב המרה לקבוצה.
          </p>
        </header>

        {/* Pill tabs */}
        {all.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {buckets.map((b) => {
              const count = b === "all" ? all.length : all.filter((c) => c.status === b).length;
              const label = b === "all" ? "הכל" : b;
              const isActive = active === b;
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => setActive(b)}
                  className={
                    "inline-flex items-center gap-2 rounded-full h-10 px-5 text-sm font-medium transition-colors " +
                    (isActive
                      ? "bg-stone-900 text-stone-50"
                      : "bg-stone-200/70 text-stone-700 hover:bg-stone-200")
                  }
                >
                  <span>{label}</span>
                  <span
                    className={
                      "rounded-full px-2 py-0.5 text-[11px] tabular-nums " +
                      (isActive ? "bg-stone-50/15 text-stone-50" : "bg-white/80 text-stone-600")
                    }
                    dir="ltr"
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-stone-500">טוען…</p>
        ) : filtered.length === 0 ? (
          <ColorBlock tint="stone" className="text-center" size="lg">
            <p className="text-base sm:text-lg font-bold tracking-tight text-stone-900">אין שיחות בקטגוריה זו.</p>
          </ColorBlock>
        ) : (
          <div className="rounded-[24px] bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {["#", "תלמיד", "קבוצה", "מקור", "טלפון", "סטטוס", "הערה"].map((h) => (
                    <th key={h} className="text-start px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const dot = (c.status && STATUS_DOT[c.status]) ?? "bg-stone-400";
                  return (
                    <tr key={c.id} className="border-t border-stone-200/70 hover:bg-stone-50 transition-colors">
                      <td className="px-6 py-4 text-stone-500 tabular-nums" dir="ltr">{c.call_num ?? "—"}</td>
                      <td className="px-6 py-4 font-medium text-stone-900">{c.student_name}</td>
                      <td className="px-6 py-4 text-stone-600">{c.group_name ?? "—"}</td>
                      <td className="px-6 py-4 text-stone-600">{c.source ?? "—"}</td>
                      <td className="px-6 py-4 text-stone-600 tabular-nums" dir="ltr">{c.phone ?? "—"}</td>
                      <td className="px-6 py-4">
                        {c.status && (
                          <span className="inline-flex items-center gap-2 text-stone-700">
                            <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                            {c.status}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs text-stone-500">{c.note ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
